import { useState } from 'react';
import ReactSelect from 'react-select';

import { Button } from '@/components/ui/button';
import styles from '../UnifyOnboarding.module.scss';

/**
 * Outlet picker for multi-outlet POSes (NEA-5988). Shown after the OAuth
 * exchange returns the account's outlet list — ingest is withheld until the
 * retailer picks which outlet this store is, so a multi-outlet account can
 * never ingest every outlet merged.
 */
export const OutletPicker = ({ locations, currentLocationId, providerLabel, submitting, error, onSubmit, onCancel }) => {
	const options = locations.map((location) => {
		const baseLabel = location.description
			? `${location.locationName} — ${location.description}`
			: location.locationName || location.locationId;
		// An outlet already mapped to an org sibling is unselectable (mirrors the
		// portal LocationMapper's dedupe) — two stores on one outlet would show
		// shoppers the same branch's stock twice. The current outlet stays
		// selectable even if a legacy duplicate exists, so "keep" always works.
		const takenBySibling = (location.mappedToShopId || location.mappedToShopName) && location.locationId !== currentLocationId;
		return {
			value: location.locationId,
			label: takenBySibling ? `${baseLabel} — linked to ${location.mappedToShopName || 'another store'}` : baseLabel,
			isDisabled: !!takenBySibling
		};
	});
	const [selected, setSelected] = useState(() => options.find((option) => option.value === currentLocationId) || null);

	return (
		<div className="space-y-4" data-cy="outlet-picker">
			<div>
				<h4>Which outlet is this store?</h4>
				<p className={styles.helperText}>
					Your {providerLabel || 'POS'} account has more than one outlet. Pick the one for this store so we only sync its own
					stock — choosing the wrong outlet would show shoppers another branch&apos;s products.
				</p>
			</div>

			<ReactSelect
				id="outlet-select"
				placeholder="Select an outlet..."
				value={selected}
				onChange={setSelected}
				options={options}
				isDisabled={submitting}
				isClearable={false}
				isSearchable={true}
				components={{ IndicatorSeparator: () => null }}
				styles={{
					control: (provided, state) => ({
						...provided,
						borderRadius: '0.25rem',
						borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
						boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
						'&:hover': {
							borderColor: state.isFocused ? '#6366f1' : '#9ca3af'
						}
					}),
					container: (provided) => ({
						...provided,
						width: '20rem',
						maxWidth: '100%'
					})
				}}
			/>

			{error && <div className={styles.warning}>{error}</div>}

			<div className="flex items-center gap-3">
				<Button
					onClick={() => selected && onSubmit(selected.value)}
					disabled={!selected || submitting}
					data-cy="outlet-picker-submit"
				>
					{submitting ? 'Saving...' : 'Confirm outlet'}
				</Button>
				{onCancel && (
					<button type="button" className={styles.linkButton} onClick={onCancel} disabled={submitting}>
						{currentLocationId ? 'Keep current outlet' : 'Cancel'}
					</button>
				)}
			</div>
		</div>
	);
};
